<template>
  <div
    class="player-controls"
    :class="{ 'player-controls--over-media': overMedia }"
    role="group"
    aria-label="Player controls"
    @click.stop
  >
    <Button
      class="player-button"
      :class="{ 'player-button--play': !playing }"
      variant="ghost"
      size="lg"
      :icon="playing ? PauseIcon : PlayIcon"
      :aria-label="playing ? 'Pause' : 'Play'"
      :disabled="disabled"
      @click="$emit('togglePlayback')"
    />
    <div
      ref="volumeControlsRef"
      class="player-volume"
      :class="{
        'player-volume--expanded': volumeExpanded,
        'player-volume--adjusting': isAdjustingVolume,
      }"
      @pointerenter="onVolumePointerEnter"
      @pointerleave="onVolumePointerLeave"
      @pointerdown="onVolumePointerDown"
      @focusin="onVolumeFocusIn"
      @keydown="hasVolumeKeyboardFocus = true"
      @focusout="onVolumeFocusOut"
    >
      <Button
        class="player-button"
        variant="ghost"
        size="lg"
        :icon="volumeIcon"
        :aria-label="muted || volume === 0 ? 'Unmute' : 'Mute'"
        :aria-pressed="muted || volume === 0"
        :disabled="disabled"
        @click="$emit('toggleMute')"
      />
      <div class="player-volume-slider">
        <input
          class="player-range player-volume-range"
          type="range"
          aria-label="Volume"
          min="0"
          max="1"
          step="0.01"
          :value="muted ? 0 : volume"
          :aria-valuetext="`${Math.round((muted ? 0 : volume) * 100)}%`"
          :style="{ '--volume': muted ? 0 : volume }"
          :disabled="disabled"
          @pointerdown="isAdjustingVolume = true"
          @input="onVolume"
        />
      </div>
    </div>
    <div class="player-time-controls">
      <span class="player-time" :style="{ width: currentTimeWidth }">
        <span
          ref="currentTimeLabel"
          class="player-time-value player-time-measure"
          aria-hidden="true"
          >{{ formatTime(duration) }}</span
        >
        <span class="player-time-value">{{ formatTime(displayTime) }}</span>
      </span>
      <div class="player-timeline">
        <div class="player-track" aria-hidden="true">
          <span
            v-for="(range, index) in buffered"
            :key="index"
            class="player-buffer"
            :style="{
              left: `${range.start}%`,
              width: `${range.end - range.start}%`,
            }"
          />
          <span
            v-if="seekHoverProgress !== null && !disabled && duration"
            class="player-seek-preview"
            :style="{ width: `${seekHoverProgress}%` }"
          />
          <span class="player-progress" :style="{ width: `${progress}%` }" />
        </div>
        <input
          class="player-range player-seek"
          :class="{ 'player-seek--dragging': seekPointerId !== null }"
          type="range"
          aria-label="Seek"
          min="0"
          :max="duration || 1"
          step="0.1"
          :value="displayTime"
          :aria-valuetext="`${formatTime(displayTime)} of ${formatTime(duration)}`"
          :disabled="disabled || !duration"
          @pointerdown="onSeekPointerDown"
          @pointerenter="onSeekPointerMove"
          @pointermove="onSeekPointerMove"
          @pointerup="onSeekPointerUp"
          @pointerleave="seekHoverProgress = null"
          @pointercancel="onSeekPointerCancel"
          @lostpointercapture="onSeekPointerCancel"
          @input="onSeek"
        />
      </div>
      <span class="player-time" :style="{ width: remainingTimeWidth }">
        <span
          ref="remainingTimeLabel"
          class="player-time-value player-time-measure"
          aria-hidden="true"
          >-{{ formatTime(duration) }}</span
        >
        <span class="player-time-value"
          >-{{ formatTime(Math.max(0, duration - displayTime)) }}</span
        >
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { Button } from '@dacr/library'
import PauseIcon from './PlayerPauseIcon.vue'
import PlayIcon from './PlayerPlayIcon.vue'
import { VolumeHighIcon, VolumeLowIcon, VolumeOffIcon } from '@dacr/icons'

const props = defineProps<{
  playing: boolean
  currentTime: number
  duration: number
  volume: number
  muted: boolean
  buffered: { start: number; end: number }[]
  disabled: boolean
  overMedia?: boolean
}>()

const emit = defineEmits<{
  togglePlayback: []
  toggleMute: []
  seek: [time: number]
  volume: [volume: number]
}>()

const volumeControlsRef = ref<HTMLElement | null>(null)
const isVolumePointerInside = ref(false)
const hasVolumeKeyboardFocus = ref(false)
const volumeExpanded = ref(false)
const isAdjustingVolume = ref(false)
const volumeIcon = computed(() => {
  if (props.muted || props.volume === 0) return VolumeOffIcon
  return props.volume < 0.5 ? VolumeLowIcon : VolumeHighIcon
})

const onVolumePointerEnter = (event: PointerEvent) => {
  if (
    event.pointerType === 'mouse' &&
    window.matchMedia('(hover: hover) and (pointer: fine)').matches
  ) {
    isVolumePointerInside.value = true
    volumeExpanded.value = true
  }
}

const collapseVolumeIfIdle = () => {
  if (
    !isVolumePointerInside.value &&
    !isAdjustingVolume.value &&
    !hasVolumeKeyboardFocus.value
  )
    volumeExpanded.value = false
}

const onVolumePointerDown = () => {
  hasVolumeKeyboardFocus.value = false
  isVolumePointerInside.value = true
  volumeExpanded.value = true
}

const onVolumePointerLeave = (event: PointerEvent) => {
  if (event.pointerType !== 'mouse') return
  isVolumePointerInside.value = false
  collapseVolumeIfIdle()
}

const onVolumeFocusIn = () => {
  volumeExpanded.value = true
  if (!isVolumePointerInside.value) hasVolumeKeyboardFocus.value = true
}

const onVolumeFocusOut = (event: FocusEvent) => {
  if (
    event.relatedTarget instanceof Node &&
    volumeControlsRef.value?.contains(event.relatedTarget)
  )
    return
  hasVolumeKeyboardFocus.value = false
  queueMicrotask(collapseVolumeIfIdle)
}

const endVolumeAdjustment = (event: PointerEvent) => {
  if (event.pointerType === 'mouse') {
    const bounds = volumeControlsRef.value?.getBoundingClientRect()
    isVolumePointerInside.value =
      !!bounds &&
      event.clientX >= bounds.left &&
      event.clientX <= bounds.right &&
      event.clientY >= bounds.top &&
      event.clientY <= bounds.bottom
  }
  isAdjustingVolume.value = false
  collapseVolumeIfIdle()
}

const onOutsidePointerDown = (event: PointerEvent) => {
  if (
    event.target instanceof Node &&
    volumeControlsRef.value?.contains(event.target)
  )
    return
  isVolumePointerInside.value = false
  hasVolumeKeyboardFocus.value = false
  collapseVolumeIfIdle()
}

const resetVolumeControls = () => {
  hasVolumeKeyboardFocus.value = false
  isVolumePointerInside.value = false
  isAdjustingVolume.value = false
  volumeExpanded.value = false
}

const currentTimeLabel = ref<HTMLElement | null>(null)
const remainingTimeLabel = ref<HTMLElement | null>(null)
const currentTimeWidth = ref<string>()
const remainingTimeWidth = ref<string>()
let timeObserver: ResizeObserver | undefined

onMounted(() => {
  window.addEventListener('pointerdown', onOutsidePointerDown)
  window.addEventListener('pointerup', endVolumeAdjustment, true)
  window.addEventListener('pointercancel', endVolumeAdjustment, true)
  window.addEventListener('blur', resetVolumeControls)
  window.addEventListener('blur', resetSeek)
  const measure = () => {
    if (currentTimeLabel.value)
      currentTimeWidth.value = `${Math.ceil(currentTimeLabel.value.getBoundingClientRect().width)}px`
    if (remainingTimeLabel.value)
      remainingTimeWidth.value = `${Math.ceil(remainingTimeLabel.value.getBoundingClientRect().width)}px`
  }
  timeObserver = new ResizeObserver(measure)
  if (currentTimeLabel.value) timeObserver.observe(currentTimeLabel.value)
  if (remainingTimeLabel.value) timeObserver.observe(remainingTimeLabel.value)
  measure()
})

onUnmounted(() => {
  timeObserver?.disconnect()
  window.removeEventListener('pointerdown', onOutsidePointerDown)
  window.removeEventListener('pointerup', endVolumeAdjustment, true)
  window.removeEventListener('pointercancel', endVolumeAdjustment, true)
  window.removeEventListener('blur', resetVolumeControls)
  window.removeEventListener('blur', resetSeek)
  resetSeek()
})

const seekPointerId = ref<number | null>(null)
const seekTime = ref<number | null>(null)
let seekInput: HTMLInputElement | null = null
const displayTime = computed(() => seekTime.value ?? props.currentTime)

const progress = computed(() =>
  props.duration ? Math.min(100, (displayTime.value / props.duration) * 100) : 0
)

const seekHoverProgress = ref<number | null>(null)

const resetSeek = () => {
  const pointerId = seekPointerId.value
  const input = seekInput
  seekPointerId.value = null
  seekInput = null
  seekTime.value = null
  seekHoverProgress.value = null
  if (pointerId !== null && input?.hasPointerCapture(pointerId)) {
    input.releasePointerCapture(pointerId)
  }
}

watch([() => props.disabled, () => props.duration], resetSeek)

const seekFromPointer = (event: PointerEvent, input: HTMLInputElement) => {
  const bounds = input.parentElement?.getBoundingClientRect()
  if (!bounds?.width) return
  const fraction = Math.max(
    0,
    Math.min(1, (event.clientX - bounds.left) / bounds.width)
  )
  input.valueAsNumber = Math.round(fraction * props.duration * 10) / 10
  if (seekPointerId.value !== null) seekTime.value = input.valueAsNumber
  emit('seek', input.valueAsNumber)
}

const onSeekPointerDown = (event: PointerEvent) => {
  if (
    props.disabled ||
    !props.duration ||
    seekPointerId.value !== null ||
    event.button !== 0 ||
    !event.isPrimary ||
    !(event.currentTarget instanceof HTMLInputElement)
  )
    return
  if (event.pointerType !== 'mouse') {
    event.preventDefault()
    seekInput = event.currentTarget
    seekPointerId.value = event.pointerId
    seekInput.setPointerCapture(event.pointerId)
  }
  seekFromPointer(event, event.currentTarget)
}

const onSeekPointerMove = (event: PointerEvent) => {
  if (seekPointerId.value === event.pointerId && seekInput) {
    seekFromPointer(event, seekInput)
    return
  }
  if (
    event.pointerType === 'touch' ||
    !(event.currentTarget instanceof HTMLInputElement)
  )
    return
  const bounds = event.currentTarget.parentElement?.getBoundingClientRect()
  if (!bounds) return
  seekHoverProgress.value =
    bounds.width > 0 &&
    event.clientX >= bounds.left &&
    event.clientX <= bounds.right &&
    event.clientY >= bounds.top &&
    event.clientY <= bounds.bottom
      ? ((event.clientX - bounds.left) / bounds.width) * 100
      : null
}

const onSeekPointerUp = (event: PointerEvent) => {
  if (seekPointerId.value !== event.pointerId || !seekInput) return
  seekFromPointer(event, seekInput)
  resetSeek()
}

const onSeekPointerCancel = (event: PointerEvent) => {
  if (seekPointerId.value === event.pointerId) resetSeek()
}

const formatTime = (seconds: number) => {
  const total = Math.max(0, Math.floor(seconds))
  const minutes = Math.floor(total / 60)
  const remainder = String(total % 60).padStart(2, '0')
  return minutes >= 60
    ? `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, '0')}:${remainder}`
    : `${minutes}:${remainder}`
}

const onSeek = (event: Event) => {
  if (seekPointerId.value !== null) return
  if (event.target instanceof HTMLInputElement)
    emit('seek', event.target.valueAsNumber)
}

const onVolume = (event: Event) => {
  if (event.target instanceof HTMLInputElement)
    emit('volume', event.target.valueAsNumber)
}
</script>

<style scoped>
.player-controls {
  position: absolute;
  inset: auto var(--player-controls-inset, 8px)
    var(--player-controls-inset, 8px);
  display: flex;
  align-items: center;
  gap: 1px;
  height: 44px;
  padding: 4px;
  border-radius: 999px;
  color: white;
  background:
    linear-gradient(rgb(0 0 0 / 40%), rgb(0 0 0 / 40%)), rgb(255 255 255 / 10%);
  box-shadow:
    inset 0 1px rgb(255 255 255 / 10%),
    inset 0 0 0 1px rgb(255 255 255 / 5%);
  backdrop-filter: blur(16px) saturate(1.5);
  -webkit-backdrop-filter: blur(16px) saturate(1.5);
}

.player-controls--over-media {
  box-shadow: none;
}

.player-button {
  position: relative;
  flex: 0 0 36px;
  width: 36px;
  height: 36px;
  padding: 8px;
  color: white;
  border-radius: 50%;
}

.player-button:hover {
  background: rgb(255 255 255 / 10%);
}

.player-button:disabled {
  opacity: 0.5;
}

.player-button :deep(svg) {
  pointer-events: none;
  filter: drop-shadow(0 1px rgb(0 0 0 / 15%));
}

.player-button--play :deep(svg) {
  transform: translateX(1px);
}

.player-volume {
  display: flex;
  align-items: center;
}

.player-volume-slider {
  width: 0;
  overflow: hidden;
  opacity: 0;
  transition:
    width 150ms ease,
    opacity 150ms ease;
}

.player-volume--expanded .player-volume-slider {
  width: 96px;
  opacity: 1;
}

.player-volume--adjusting .player-button,
.player-volume:has(.player-volume-range:focus-visible) .player-button {
  background: transparent;
}

.player-time-controls {
  display: flex;
  flex: 1;
  align-items: center;
  gap: 10px;
  min-width: 0;
  padding: 0 12px;
}

.player-time {
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 400;
  height: 20px;
  line-height: 19.5px;
  letter-spacing: 1px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  text-shadow: 0 1px rgb(0 0 0 / 15%);
}

.player-time-value {
  display: block;
  line-height: 20px;
  width: max-content;
  transform: translateY(-0.5px);
}

.player-time-measure {
  position: absolute;
  visibility: hidden;
  pointer-events: none;
}

.player-timeline {
  position: relative;
  flex: 1;
  min-width: 16px;
  height: 32px;
}

.player-track,
.player-buffer,
.player-seek-preview,
.player-progress {
  position: absolute;
  height: 4px;
  border-radius: 2px;
}

.player-track {
  inset: 14px 0;
  overflow: hidden;
  background: rgb(255 255 255 / 20%);
}

.player-buffer {
  background: rgb(255 255 255 / 20%);
}

.player-seek-preview {
  background: rgb(255 255 255 / 30%);
}

.player-progress {
  background: white;
}

.player-range {
  display: block;
  width: 100%;
  height: 32px;
  margin: 0;
  appearance: none;
  background: transparent;
  cursor: pointer;
}

.player-range:disabled {
  pointer-events: none;
}

.player-volume-range {
  width: 72px;
  margin: 0 12px;
}

.player-seek {
  position: absolute;
  top: -6px;
  width: calc(100% + 12px);
  height: 44px;
  margin-left: -6px;
  touch-action: none;
  user-select: none;
  -webkit-user-select: none;
}

.player-range::-webkit-slider-runnable-track {
  height: 4px;
  border-radius: 2px;
}

.player-range::-moz-range-track {
  height: 4px;
  border-radius: 2px;
  background: transparent;
}

.player-volume-range::-webkit-slider-runnable-track {
  background: linear-gradient(
    to right,
    white calc(6px + (100% - 12px) * var(--volume)),
    rgb(255 255 255 / 20%) 0
  );
}

.player-volume-range::-moz-range-track {
  background: linear-gradient(
    to right,
    white calc(6px + (100% - 12px) * var(--volume)),
    rgb(255 255 255 / 20%) 0
  );
}

.player-range::-webkit-slider-thumb {
  width: 12px;
  height: 12px;
  margin-top: -4px;
  appearance: none;
  border: 0;
  border-radius: 50%;
  background: white;
  box-shadow:
    0 0 0 1px rgb(0 0 0 / 10%),
    0 1px 3px rgb(0 0 0 / 35%),
    0 1px 2px -1px rgb(0 0 0 / 35%);
}

.player-range::-moz-range-thumb {
  width: 12px;
  height: 12px;
  border: 0;
  border-radius: 50%;
  background: white;
  box-shadow:
    0 0 0 1px rgb(0 0 0 / 10%),
    0 1px 3px rgb(0 0 0 / 35%),
    0 1px 2px -1px rgb(0 0 0 / 35%);
}

.player-seek:not(:hover):not(:focus-visible):not(
    .player-seek--dragging
  )::-webkit-slider-thumb {
  opacity: 0;
}

.player-seek:not(:hover):not(:focus-visible):not(
    .player-seek--dragging
  )::-moz-range-thumb {
  opacity: 0;
}

.player-range:focus-visible,
.player-button:focus-visible {
  outline: 2px solid white;
  outline-offset: 2px;
}

.player-volume-range:focus-visible {
  outline: none;
}

.player-volume-range:focus-visible::-webkit-slider-thumb {
  outline: 2px solid white;
  outline-offset: 3px;
}

.player-volume-range:focus-visible::-moz-range-thumb {
  outline: 2px solid white;
  outline-offset: 3px;
}

@container player (max-width: 380px) {
  .player-volume--expanded .player-volume-slider {
    width: 64px;
  }

  .player-volume-range {
    width: 48px;
    margin: 0 8px;
  }

  .player-time-controls {
    gap: 6px;
    padding: 0 8px;
  }
}

@media (width < 768px), (pointer: coarse) {
  .player-volume {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .player-volume-slider {
    transition: none;
  }
}
</style>
